import React, { useRef, useState } from 'react';
import { css } from '@emotion/react';
import AppBar from '@mui/material/AppBar';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Toolbar from '@mui/material/Toolbar';

import { analytics } from '~/common';
import { useCurrentUser } from '~/src/common/user';
import { useIsSSR } from '~/src/components/hooks/useIsSSR';
import { breakpoints } from '~/src/styles';
import { headerZIndex } from '~/src/styles/styled-variables';

import { DefaultNav } from './DefaultNav';
import { SideNav } from './SideNav';
import { SimpleNav } from './SimpleNav';
import { SimpleNavWithSignIn } from './SimpleNavWithSignIn';
import { UserMenu } from './UserMenu';
import { PrintHidden } from '../PrintHidden';

interface HeaderProps {
  kind?: 'hideTopNav' | 'simpleNavWithSignIn' | 'simpleNav' | 'hideDefaultSideNav';
}

const styles = {
  appBar: css({
    zIndex: `${headerZIndex}`,
    background: '#fff',
    boxShadow: '0px 4px 20px 0px rgba(0, 0, 0, 0.04)',
    [breakpoints.minTablet]: {
      boxShadow: 'none',
    },
  }),
  appBarForAuthorizedUser: css({
    boxShadow: '0px 4px 20px 0px #0000000A;',
    [breakpoints.minTablet]: {
      boxShadow: '0px 4px 20px 0px #0000000A;',
    },
  }),
  toolbar: css({
    height: 'var(--Header-Height)',
    padding: '0 16px',
    [breakpoints.minTablet]: {
      padding: '0 24px',
    },
  }),
};

export const Header: React.FC<HeaderProps> = ({ kind }) => {
  const [isSideNavShown, setIsSideNavShown] = useState(false);
  const [isShowUserMenu, setIsShowUserMenu] = useState(false);
  const { isLoggedIn, loading } = useCurrentUser();
  const isSSR = useIsSSR();

  const isUserAuthorized = !isSSR && !loading && isLoggedIn;

  const userMenuRef = useRef<HTMLDivElement>(null);
  const sideNavRef = useRef<HTMLDivElement>(null);

  const toggleUserMenu = () => {
    if (isSideNavShown) {
      setIsSideNavShown(false);
    }
    setIsShowUserMenu(state => !state);
  };
  const toggleSideNav = (event: React.KeyboardEvent) => {
    if (isShowUserMenu) {
      setIsShowUserMenu(false);
    }
    if (
      event.type === 'keydown' &&
      ((event as React.KeyboardEvent).key === 'Tab' ||
        (event as React.KeyboardEvent).key === 'Shift')
    ) {
      return;
    }
    if (!isSideNavShown) {
      analytics.sendLeftNavClicked();
    }
    setIsSideNavShown(state => !state);
  };

  const showSideNav = () => {
    setIsSideNavShown(true);
  };
  const hideSideNav = () => {
    setIsSideNavShown(false);
  };

  const handleUserMenuOutsideClick = () => (event: MouseEvent | TouchEvent) => {
    if (userMenuRef.current?.contains(event.target as Node)) {
      return;
    }
    setIsShowUserMenu(false);
  };

  const handleSideNavOutsideClick = () => (e: MouseEvent | TouchEvent) => {
    // ignore clicks on the side nav component itself
    if (sideNavRef.current?.contains(e.target as Node)) {
      return;
    }
    setIsSideNavShown(false);
  };

  const renderHeader = () => {
    switch (kind) {
      case 'hideTopNav':
        return '';
      case 'simpleNav':
        return <SimpleNav />;
      case 'simpleNavWithSignIn':
        return <SimpleNavWithSignIn />;
      case 'hideDefaultSideNav':
        return (
          <ClickAwayListener onClickAway={handleSideNavOutsideClick()}>
            <div ref={sideNavRef} style={{ width: '100%' }}>
              <DefaultNav
                toggleSideNav={toggleSideNav}
                toggleUserMenu={toggleUserMenu}
                hideSideNav={hideSideNav}
              />
              <div onMouseEnter={() => setIsSideNavShown(true)} onMouseLeave={hideSideNav}>
                <SideNav
                  showSideNav={isSideNavShown}
                  toggleSideNav={toggleSideNav}
                  hideDefaultSideNav={true}
                />
              </div>
            </div>
          </ClickAwayListener>
        );
      default:
        return (
          <ClickAwayListener onClickAway={handleSideNavOutsideClick()}>
            <div ref={sideNavRef} style={{ width: '100%' }}>
              <DefaultNav
                toggleSideNav={toggleSideNav}
                toggleUserMenu={toggleUserMenu}
                hideSideNav={hideSideNav}
              />
              <div onMouseEnter={showSideNav} onMouseLeave={hideSideNav}>
                <SideNav showSideNav={isSideNavShown} toggleSideNav={toggleSideNav} />
              </div>
            </div>
          </ClickAwayListener>
        );
    }
  };

  return (
    <PrintHidden>
      {kind !== 'hideTopNav' && (
        <>
          <AppBar
            color="default"
            css={[styles.appBar, isUserAuthorized ? styles.appBarForAuthorizedUser : null]}
          >
            <Toolbar ref={userMenuRef} css={styles.toolbar}>
              {renderHeader()}
            </Toolbar>
          </AppBar>
          <ClickAwayListener onClickAway={handleUserMenuOutsideClick()}>
            <div>
              <UserMenu showUserMenu={isShowUserMenu} toggleUserMenu={toggleUserMenu} />
            </div>
          </ClickAwayListener>
        </>
      )}
    </PrintHidden>
  );
};
